import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useState,
} from 'react';

import { WSocketConnectionState } from '../utils/WSocket';

import { assert } from '../utils/assert';

import { useLocalStorageState } from './useLocalStorageState';
import { useWSocket } from './useWSocket';

interface BaseCommand {
    name: string;
    hash_code: number;
    categories: Array<string>;
    information?: string;
}
interface InputFieldCommand extends BaseCommand {
    command_type: 'InputField';
}
interface ButtonCommand extends BaseCommand {
    command_type: 'Button';
}
interface LabelCommand extends BaseCommand {
    command_type: 'Label';
}
interface ButtonGroupCommand extends BaseCommand {
    command_type: 'ButtonGroup';
    names: Array<string>;
    options: Array<unknown>;
}
interface PopupCommand extends BaseCommand {
    command_type: 'Popup';
    children: Array<QAWebToolCommand>;
}
type QAWebToolCommand =
    | InputFieldCommand
    | ButtonCommand
    | LabelCommand
    | ButtonGroupCommand
    | PopupCommand;

interface LogsMessage {
    id: number;
    command_type: 'Logs';
    data: {
        condition: string;
        stacktace: string;
        type: string;
        timestamp: string;
    };
}
interface InitializationMessage {
    id: number;
    command_type: 'Initialization';
    data: Array<QAWebToolCommand>;
}
type QAWebToolMessage = LogsMessage | InitializationMessage;

const isQAWebToolMessage = (data: unknown): data is QAWebToolMessage => {
    return (
        data !== null &&
        typeof data === 'object' &&
        'command_type' in data &&
        'id' in data
    );
};

export interface QAWebTool {
    socketState: WSocketConnectionState;
    isReady: boolean;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendCommand: (name: string, data: Array<any>) => void;
    connect: () => void;
    disconnect: () => void;
    ipAddress: string;
    setIpAddress: (value: string) => void;
}

const QAWebToolContext = createContext<QAWebTool | null>(null);

export const QAWebToolProvider = ({ children }: PropsWithChildren) => {
    const [ipAddress, setIpAddress] = useLocalStorageState(
        'localhost',
        'live-preview-ip',
    );

    const [commandMap, setCommandMap] = useState<Map<
        string,
        QAWebToolCommand
    > | null>(null);

    const { connectionState, socket } = useWSocket(
        `ws://${ipAddress}:9876`,
        (ev: MessageEvent) => {
            if (typeof ev.data !== 'string') {
                return;
            }
            const message = JSON.parse(ev.data);
            if (!isQAWebToolMessage(message)) {
                return;
            }

            if (message.command_type === 'Initialization') {
                const commandMap = new Map<string, QAWebToolCommand>(
                    message.data.map((command) => [command.name, command]),
                );
                setCommandMap(commandMap);
            }
        },
        [],
    );

    const isReady = commandMap !== null && connectionState === 'connected';
    const sendCommand = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (name: string, data: Array<any>) => {
            if (!commandMap) {
                return;
            }

            const command = commandMap.get(name);
            if (!command) {
                return;
            }

            socket?.sendMessage({
                id: command.hash_code,
                command_type: command.command_type,
                data,
            });
        },
        [socket?.sendMessage, commandMap],
    );

    const connect = useCallback(() => {
        socket?.connect();
    }, [socket]);

    const disconnect = useCallback(() => {
        socket?.disconnect();
    }, [socket]);

    const value = {
        socketState: connectionState,
        isReady,
        sendCommand,
        connect,
        disconnect,
        ipAddress: ipAddress,
        setIpAddress: setIpAddress,
    };

    return (
        <QAWebToolContext.Provider value={value}>
            {children}
        </QAWebToolContext.Provider>
    );
};

/**
 * Hook for interacting with the QA Web Tool: a way to connect to dev builds of
 * the game to invoke QA Tool commands. The web tool also sends a list of
 * available commands, but these are not exposed in the hook right now. The web
 * tool can also send logs, which is also not exposed in this hook.
 *
 * Note: Must be wrapped in QAWebToolProvider.
 *
 * @see {@link QAWebToolProvider}
 *
 * @example
 * Simple example showing how to send commands
 * ```
 * const MyComponent = () => {
 *    const webTool = useQAWebTool()
 *
 *    // Send a command every second
 *    useEffect(() => {
 *        const timeoutHandle = setTimeout(() => { webTool.sendCommand("My Command", ["my data"]) }, 1000)
 *
 *        return () => clearTimeout(timeoutHandle)
 *    }, [posts, sendCommand])
 *
 *    return <div>
 *        <p>State: {webTool.socketState}</p>
 *        <button onClick={() => webTool.connect()}>Connect</button>
 *        <button onClick={() => webTool.disconnect()}>Disconnect</button>
 *    </div>
 * }
 * ```
 */
export const useQAWebTool = () => {
    const context = useContext(QAWebToolContext);

    assert(
        context,
        'useQAWebTool must be used within the context of <QAWebToolProvider />',
    );

    return context;
};
